import React, { useEffect, useRef } from "react";
import { ChevronRight, Mic, MicOff, PhoneOff } from "lucide-react";
import { AURA_API } from "../../lib/aura";
import { AuraOrb } from "./AuraChat";
import { AVATAR_VIDEO_ID, useAura } from "./AuraProvider";
import ChromaKeyCanvas from "./ChromaKeyCanvas";
import SegmentedAvatar from "./SegmentedAvatar";

// Idle clips played (in turn) before a conversation starts, e.g. REACT_APP_AURA_IDLE_CLIPS=/aura/idle_1.mp4,/aura/idle_2.mp4
// (files in frontend/public/aura/; use your own licensed videos of the same person as the Anam avatar). If
// none are set, the Anam avatar's own idle loop is used. Once the conversation starts, her live stream takes over.
const IDLE_CLIPS = (process.env.REACT_APP_AURA_IDLE_CLIPS || "")
  .split(",")
  .map((s) => s.trim())
  .filter(Boolean);

// Building blocks of Docket-style "Frameless" Simmi: the avatar itself and the control pill.

// Docket's control pill: mic, hang up, and "›" to continue in text.
export const ControlPill = () => {
  const { muted, toggleMute, endCall, setOpen } = useAura();
  return (
    <div className="aura-pill flex items-center gap-2 rounded-full p-2">
      <button
        onClick={toggleMute}
        aria-label={muted ? "Unmute microphone" : "Mute microphone"}
        className={`flex h-12 w-12 items-center justify-center rounded-[14px] transition-colors ${
          muted ? "bg-white text-black" : "bg-white/10 text-white hover:bg-white/15"
        }`}
      >
        {muted ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
      </button>
      <button
        onClick={endCall}
        aria-label="End conversation"
        className="flex h-12 w-12 items-center justify-center rounded-[14px] bg-[#7F1D2D] text-rose-300 transition-colors hover:bg-[#9F1239]"
      >
        <PhoneOff className="h-5 w-5" />
      </button>
      <button
        onClick={() => {
          endCall();
          setOpen(true);
        }}
        aria-label="Continue in chat"
        title="Continue in chat"
        className="flex h-12 w-10 items-center justify-center text-white/60 transition-colors hover:text-white"
      >
        <ChevronRight className="h-5 w-5" />
      </button>
    </div>
  );
};

// The avatar: green-screen keyed onto the page when the avatar supports it, else a soft-edged video.
// `live` renders the element the Anam stream attaches to; the idle loop stays underneath until it plays.
export const AvatarMedia = ({ preview, keyed, live, liveVisible, muted = false, sheen = false }) => {
  const idleRef = useRef(null);
  const liveRef = useRef(null);
  const media = keyed ? "aura-stage-keyed" : "aura-stage-media";

  // The speaker button mutes Simmi's voice (the live stream's audio plays through this video element).
  useEffect(() => {
    const video = document.getElementById(AVATAR_VIDEO_ID);
    if (video) video.muted = muted;
  }, [muted, live]);

  if (keyed) {
    return (
      <>
        <video
          ref={idleRef}
          src={`${AURA_API}/avatar/idle.mp4`}
          crossOrigin="anonymous"
          autoPlay
          muted
          loop
          playsInline
          className="aura-hidden-video"
        />
        <ChromaKeyCanvas videoRef={idleRef} className={`${media} ${live && liveVisible ? "opacity-0" : "opacity-100"}`} />
        {live && (
          <>
            <video id={AVATAR_VIDEO_ID} ref={liveRef} autoPlay playsInline muted={muted} className="aura-hidden-video" />
            <ChromaKeyCanvas
              videoRef={liveRef}
              className={`${media} transition-opacity duration-700 ${liveVisible ? "opacity-100" : "opacity-0"}`}
            />
          </>
        )}
      </>
    );
  }

  // Anam's own idle loop of the configured avatar (blinking, breathing), then her live stream while she talks,
  // both cut out of their studio background in the browser (Docket-style, no box). The orb shows only if the
  // avatar isn't available at all.
  if (!preview?.idle_video_url && !IDLE_CLIPS.length) {
    return (
      <div className="absolute inset-0 flex items-center justify-center">
        <AuraOrb size={120} />
      </div>
    );
  }
  return (
    <SegmentedAvatar
      idleSrcs={IDLE_CLIPS.length ? IDLE_CLIPS : [`${AURA_API}/avatar/idle.mp4`]}
      liveActive={live}
      liveVisible={liveVisible}
      muted={muted}
      sheen={sheen}
    />
  );
};
